import React, { type ComponentType } from 'react'

import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { IconButton, Stack, TableCell, TableRow, Tooltip } from '@mui/material'
import CustomerEnrichApiQuotaResetButton from 'features/customers/components/usage/CustomerEnrichApiQuotaResetButton'
import CustomerEnrichApiQuotaUsage from 'features/customers/components/usage/CustomerEnrichApiQuotaUsage'
import CustomerRevealApiCompaniesUsage from 'features/customers/components/usage/CustomerRevealApiCompaniesUsage'
import CustomerRevealApiExtraQuotaButton from 'features/customers/components/usage/CustomerRevealApiExtraQuotaButton'
import CustomerRevealApiQuotaUsage from 'features/customers/components/usage/CustomerRevealApiQuotaUsage'
import { ENRICH_METERS, REVEAL_METERS, revealMeterLabel } from 'features/customers/constants/apiMeters'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { ENRICH_API_LIMIT_LABELS, REVEAL_API_LIMIT_LABELS } from 'features/customers/utils/features'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { Dash, LabelWithTooltip } from 'components/_base'

// Not features: these are lines on this screen. The keys are not in EFeatureNames, they have no plan
// value to differ from and nothing writes them through the features endpoint, so they cannot come
// from getCustomerFeaturesData and do not carry an override, a diff or a history.
type QuotaLine = {
  id: string
  label: string
  description: string
  Usage: ComponentType
  Action?: ComponentType
  settable: boolean
}

const CompactEnrichReset = () => <CustomerEnrichApiQuotaResetButton compact />

const RevealRequestsUsage = () => <CustomerRevealApiQuotaUsage type="requests" />
const RevealIdentifiedUsage = () => <CustomerRevealApiQuotaUsage type="identified" />
const RevealUniqueIpsUsage = () => <CustomerRevealApiQuotaUsage type="uniqueIdentifiedIps" />

const RevealRequestsTopUp = () => <CustomerRevealApiExtraQuotaButton compact type="requests" />
const RevealIdentifiedTopUp = () => <CustomerRevealApiExtraQuotaButton compact type="identified" />
const RevealUniqueIpsTopUp = () => <CustomerRevealApiExtraQuotaButton compact type="uniqueIdentifiedIps" />

export const QUOTA_LINES: QuotaLine[] = [
  {
    id: 'revealApiRequestsLimit',
    label: REVEAL_API_LIMIT_LABELS.revealApiRequestsLimit,
    description: REVEAL_METERS.requests.description,
    Usage: RevealRequestsUsage,
    Action: RevealRequestsTopUp,
    settable: true,
  },
  {
    id: 'revealApiIdentifiedLimit',
    label: REVEAL_API_LIMIT_LABELS.revealApiIdentifiedLimit,
    description: REVEAL_METERS.identified.description,
    Usage: RevealIdentifiedUsage,
    Action: RevealIdentifiedTopUp,
    settable: true,
  },
  {
    id: 'revealApiUniqueIdentifiedIpsLimit',
    label: REVEAL_API_LIMIT_LABELS.revealApiUniqueIdentifiedIpsLimit,
    description: REVEAL_METERS.uniqueIdentifiedIps.description,
    Usage: RevealUniqueIpsUsage,
    Action: RevealUniqueIpsTopUp,
    settable: true,
  },
  {
    id: 'revealApiUniqueIdentifiedCompanies',
    label: revealMeterLabel('uniqueIdentifiedCompanies'),
    description: REVEAL_METERS.uniqueIdentifiedCompanies.description,
    Usage: CustomerRevealApiCompaniesUsage,
    settable: false,
  },
  {
    id: 'enrichApiRequestsLimit',
    label: ENRICH_API_LIMIT_LABELS.enrichApiRequestsLimit,
    description: ENRICH_METERS.requests.description,
    Usage: CustomerEnrichApiQuotaUsage,
    Action: CompactEnrichReset,
    settable: true,
  },
]

const CustomerQuotaRows = () => {
  const customerId = useCustomerId()

  return (
    <>
      {QUOTA_LINES.map(({ id, label, description, Usage, Action, settable }) => (
        <TableRow hover key={id}>
          <TableCell sx={{ pl: 2 }}>
            <LabelWithTooltip label={label} tooltip={description} />
          </TableCell>

          <TableCell colSpan={2}>
            <Dash />
          </TableCell>

          <TableCell>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Usage />
              {Action && <Action />}
            </Stack>
          </TableCell>

          <TableCell colSpan={2}>
            {settable ? (
              <Tooltip title="Change the monthly limit on the API tab">
                <IconButton
                  aria-label="Open the API tab"
                  component={Link}
                  size="small"
                  to={UrlProvider.getUrl('customer', { id: customerId, tab: 'api' })}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Dash />
            )}
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export default CustomerQuotaRows
